import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Folder, Calendar, ArrowRight, Trash2, User, LogOut, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import { formatDate } from '../../lib/utils';

/**
 * Modal di conferma eliminazione progetto
 */
const DeleteConfirmModal = ({ projectName, onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
      <div className="bg-red-50 p-6 flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Eliminare il progetto?</h2>
          <p className="text-sm text-gray-600 mt-1">
            Stai per eliminare <span className="font-medium">"{projectName}"</span>.
          </p>
        </div>
      </div>
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800 font-medium mb-1">Questa operazione è irreversibile.</p>
          <p className="text-sm text-red-700">
            Verranno eliminati definitivamente il progetto e tutti i dati associati: contratti, esecuzioni e output AI generati. Non sarà possibile recuperarli.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Modal per creare nuovo progetto
 */
const NewProjectModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    initialInput: {
      country: '',
      target: '',
      vertical: '',
      brand_name: '',
      brand_positioning: '',
      research_goal: 'Identify recurring persuasive patterns',
      discovery_mode: 'parametric',
      content_type: 'UGC video ads',
      platforms: ['Facebook', 'Instagram', 'TikTok'],
      language: 'Italian',
      output_depth: 'Standard'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim() && formData.initialInput.country && formData.initialInput.target && formData.initialInput.vertical) {
      onCreate(formData);
      setFormData({
        name: '',
        description: '',
        initialInput: {
          country: '',
          target: '',
          vertical: '',
          brand_name: '',
          brand_positioning: '',
          research_goal: 'Identify recurring persuasive patterns',
          discovery_mode: 'parametric',
          content_type: 'UGC video ads',
          platforms: ['Facebook', 'Instagram', 'TikTok'],
          language: 'Italian',
          output_depth: 'Standard'
        }
      });
    }
  };

  const updateInitialInput = (field, value) => {
    setFormData(prev => ({
      ...prev,
      initialInput: {
        ...prev.initialInput,
        [field]: value
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <Card className="w-full max-w-3xl m-4">
        <CardHeader>
          <CardTitle>Nuovo Progetto - Contract 0: Research Universe</CardTitle>
          <CardDescription>
            Definisci i parametri iniziali per la ricerca e analisi del mercato
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">Informazioni Progetto</h3>
              
              <div className="space-y-2">
                <Label htmlFor="name">Nome Progetto *</Label>
                <Input
                  id="name"
                  placeholder="es. Campagna Dating App Q4 2026"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione (opzionale)</Label>
                <Textarea
                  id="description"
                  placeholder="Breve descrizione del progetto..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            {/* Contract 0 Input */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-sm font-semibold text-muted-foreground">Parametri Ricerca (Contract 0)</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Paese/Mercato *</Label>
                  <Input
                    id="country"
                    placeholder="es. Italy, Germany, USA"
                    value={formData.initialInput.country}
                    onChange={(e) => updateInitialInput('country', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vertical">Vertical/Industry *</Label>
                  <Input
                    id="vertical"
                    placeholder="es. Dating App, E-commerce, SaaS"
                    value={formData.initialInput.vertical}
                    onChange={(e) => updateInitialInput('vertical', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target">Target Audience *</Label>
                <Input
                  id="target"
                  placeholder="es. Men 45-65 interested in dating"
                  value={formData.initialInput.target}
                  onChange={(e) => updateInitialInput('target', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand_name">Brand Name (opzionale)</Label>
                  <Input
                    id="brand_name"
                    placeholder="es. Yooppe"
                    value={formData.initialInput.brand_name}
                    onChange={(e) => updateInitialInput('brand_name', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="language">Lingua</Label>
                  <Input
                    id="language"
                    placeholder="es. Italian, English"
                    value={formData.initialInput.language}
                    onChange={(e) => updateInitialInput('language', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="brand_positioning">Brand Positioning (opzionale)</Label>
                <Textarea
                  id="brand_positioning"
                  placeholder="es. Premium dating platform for mature singles"
                  value={formData.initialInput.brand_positioning}
                  onChange={(e) => updateInitialInput('brand_positioning', e.target.value)}
                  rows={2}
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="ghost" onClick={onClose}>
                Annulla
              </Button>
              <Button type="submit">
                <Plus className="w-4 h-4 mr-2" />
                Crea Progetto & Avvia Contract 0
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Card singolo progetto
 */
const ProjectCard = ({ project, onOpen, onDelete }) => {
  const contractProgress = Math.round(((project.currentContract || 0) / 26) * 100);

  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
      <CardHeader onClick={() => onOpen(project._id)} className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="group-hover:text-primary transition-colors">
              {project.name}
            </CardTitle>
            <CardDescription className="mt-2">
              {project.description || 'Nessuna descrizione'}
            </CardDescription>
          </div>
          <Badge variant={project.status === 'active' ? 'success' : 'secondary'}>
            {project.status === 'active' ? 'Attivo' : project.status === 'completed' ? 'Completato' : project.status}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent onClick={() => onOpen(project._id)}>
        <div className="space-y-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Contract {project.currentContract || 0} di 26</span>
              <span className="font-medium">{contractProgress}%</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${contractProgress}%` }}
              />
            </div>
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(project.updatedAt)}
            </div>
          </div>
        </div>
      </CardContent>

      {/* Actions */}
      <div className="border-t p-4 flex items-center justify-between">
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(project._id);
          }}
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Elimina
        </Button>
        <Button
          onClick={() => onOpen(project._id)}
          variant="default"
          size="sm"
        >
          Apri progetto
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </Card>
  );
};

/**
 * Pagina principale - Dashboard Progetti
 */
const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, loading, loadProjects, createProject, deleteProject } = useProjectStore();
  const { user, tenant, isSuperAdmin, logout } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const handleCreateProject = async (formData) => {
    try {
      const newProject = await createProject(formData);
      setIsModalOpen(false);
      // Naviga al workflow del nuovo progetto
      navigate(`/projects/${newProject._id}/workflow`);
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleOpenProject = (projectId) => {
    navigate(`/projects/${projectId}/workflow`);
  };

  const handleDeleteProject = (projectId) => {
    const project = projects.find(p => p._id === projectId);
    setDeleteTarget({ id: projectId, name: project?.name || 'questo progetto' });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProject(deleteTarget.id);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold tracking-tight">AdForge AI</h1>
              <p className="text-muted-foreground mt-2">
                {tenant?.name || 'Piattaforma di produzione video automatizzata per advertising'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* User Menu */}
              <div className="flex items-center gap-3 px-4 py-2 bg-secondary/50 rounded-lg">
                <User className="w-4 h-4" />
                <div className="text-sm">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.role}</p>
                </div>
              </div>

              {/* Admin Link (only for superadmin) */}
              {isSuperAdmin() && (
                <Link to="/admin">
                  <Button variant="outline" size="sm">
                    Admin Panel
                  </Button>
                </Link>
              )}

              {/* Logout */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>

              {/* New Project */}
              <Button onClick={() => setIsModalOpen(true)} size="lg">
                <Plus className="w-5 h-5 mr-2" />
                Nuovo Progetto
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        {loading && projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Caricamento progetti...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nessun progetto</h3>
            <p className="text-muted-foreground mb-6">
              Crea il tuo primo progetto per iniziare a produrre video con AI
            </p>
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crea il primo progetto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project._id}
                project={project}
                onOpen={handleOpenProject}
                onDelete={handleDeleteProject}
              />
            ))}
          </div>
        )}
      </div>

      {/* New Project Modal */}
      <NewProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateProject}
      />

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          projectName={deleteTarget.name}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
